// src/components/adminLogin/index.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';

const COOKIE_REMEMBER = 'vbw_remember';
const COOKIE_USERNAME = 'vbw_username';

function slugify(str = '') {
  const map = { å:'a', ä:'a', ö:'o', Å:'a', Ä:'a', Ö:'o' };
  return (
    str.replace(/[ÅÄÖåäö]/g, ch => map[ch] || ch)
      .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
      .toLowerCase().replace(/[^a-z0-9]+/g,'-')
      .replace(/^-+|-+$/g,'') || 'website'
  );
}

// --- simple cookie helpers (first-party, SameSite=Lax) ---
function setCookie(name, value, days = 180) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `; Expires=${d.toUTCString()}`;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; Path=/; SameSite=Lax${secure}`;
}
function getCookie(name) {
  const n = encodeURIComponent(name) + '=';
  const parts = document.cookie.split(';');
  for (let part of parts) {
    part = part.trim();
    if (part.startsWith(n)) return decodeURIComponent(part.substring(n.length));
  }
  return '';
}
function deleteCookie(name) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
}

function AdminLogin({ setAuthenticatedUser, t }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [error, setError] = useState('');

  // Prefill from cookies
  useEffect(() => {
    const remembered = getCookie(COOKIE_REMEMBER) === '1';
    const savedUser = getCookie(COOKIE_USERNAME);
    if (remembered) {
      setStayLoggedIn(true);
      if (savedUser) setUsername(savedUser);
    }
  }, []);

  // Optional: If we already have a valid session, skip login
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/auth/me?detailed=1');
        if (data?.authenticated && data?.user?.username) {
          setAuthenticatedUser({
            auth: true,
            name: data.user.username,
            username: data.user.username,
            websites: data.user.websites || [],
            websitesDetailed: data.user.websitesDetailed || [],
          });
          console.log(data)
          await redirectToFirstSite(data.user);
        }
      } catch {/* not logged in, ignore */}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function redirectToFirstSite(user) {
    const owner = user?.username;
    if (!owner) { setError(t('auth.noAuth')); return; }

    const det = Array.isArray(user.websitesDetailed) ? user.websitesDetailed : [];
    const basic = Array.isArray(user.websites) ? user.websites : [];

    // Prefer the detailed entry which already has { _id, title, slug }
    let site = det[0];
    if (!site) {
      // Fallback if only ids were sent (shouldn’t happen once you use ?detailed=1)
      const first = basic[0];
      site = typeof first === 'object' ? first : { slug: String(first) };
    }
    if (!site) { setError(t('auth.noAccess')); return; }

    const slug = site.slug || slugify(site.title || 'website');
    navigate(`/${owner}/${slug}`, { replace: true });
  }


  const handleLogin = async (u, p, stay) => {
    try {
      const { data } = await api.post(`/auth/login`, { username: u, password: p, stayLoggedIn: stay });
      if (data.code === '00' && data.user?.username) {
        // UI cookies (non-sensitive)
        if (stay) {
          setCookie(COOKIE_REMEMBER, '1');
          setCookie(COOKIE_USERNAME, u);
        } else {
          deleteCookie(COOKIE_REMEMBER);
          deleteCookie(COOKIE_USERNAME);
        }
     // Pull the detailed user so we get slugs immediately
     const { data: me } = await api.get('/auth/me?detailed=1');
     setAuthenticatedUser({
       auth: true,
       name: me.user.username,
       username: me.user.username,
       websites: me.user.websites || [],
       websitesDetailed: me.user.websitesDetailed || [],
     });
     setError('');
     await redirectToFirstSite(me.user);
      } else {
        setError(data.msg || 'Login failed');
      }
    } catch (err) {
      console.error(err);
      setError(t('auth.loginFailed'));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin(username, password, stayLoggedIn);
  };

  const autoLogin = () => handleLogin('admin', 'admin', true);

  // Keep cookie in sync with the toggle immediately
  function toggleStayLoggedIn() {
    const next = !stayLoggedIn;
    setStayLoggedIn(next);
    if (next) {
      setCookie(COOKIE_REMEMBER, '1');
      if (username) setCookie(COOKIE_USERNAME, username);
    } else {
      deleteCookie(COOKIE_REMEMBER);
      deleteCookie(COOKIE_USERNAME);
    }
  }

  // If username changes and we’re remembering, update cookie
  useEffect(() => {
    if (stayLoggedIn && username) setCookie(COOKIE_USERNAME, username);
  }, [stayLoggedIn, username]);

  return (
    <div className="bg-gradient-to-r from-cyan-400 to-cyan-600 p-12 flex items-center justify-center">
      <form
        className="flex flex-col items-center bg-white w-4/5 md:w-3/5 lg:w-2/5 xl:w-2/5 mx-auto p-8 rounded-lg shadow-lg"
        onSubmit={handleSubmit}
      >
        <h2 className="text-2xl text-center mb-6">{t('auth.adminPanel')}</h2>

        <div className="w-60 mx-auto mb-4">
          <label htmlFor="username" className="block mb-1 font-semibold text-gray-700">{t('auth.username')}</label>
          <input
            id="username"
            name="username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="admin-login-form-control text-sm w-full py-1 px-2 border-b-2 border-gray-400 focus:border-gray-700 outline-none"
            autoComplete="username"
            required
            placeholder={t('auth.enterUsername')}
          />
        </div>

        <div className="w-60 mx-auto mb-4">
          <label htmlFor="password" className="block mb-1 font-semibold text-gray-700">{t('auth.password')}</label>
          <input
            id="password"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="admin-login-form-control text-sm w-full py-1 px-2 border-b-2 border-gray-400 focus:border-gray-700 outline-none"
            autoComplete="current-password"
            required
            placeholder={t('auth.enterPassword')}
          />
        </div>

        <div className="w-60 mx-auto mb-6 flex items-center">
          <input
            id="stayLoggedIn"
            type="checkbox"
            checked={stayLoggedIn}
            onChange={toggleStayLoggedIn}
            className="mr-2"
          />
          <label htmlFor="stayLoggedIn" className="text-gray-700 select-none">
            {t('auth.stayLoggedIn')}
          </label>
        </div>

        {error && (
          <p className="w-60 mx-auto mb-4 text-red-600 font-semibold text-center">{error}</p>
        )}

        <input
          className="w-60 px-4 py-2 bg-gray-800 text-white border border-gray-800 rounded-full cursor-pointer hover:bg-white hover:text-gray-800"
          type="submit"
          value={t('auth.logIn')}
        />

        <button
          type="button"
          onClick={autoLogin}
          className="mt-4 w-60 px-4 py-2 text-blue-900 underline"
          aria-label="Login as admin"
        >
          {t('auth.usePanel')}
        </button>
      </form>
    </div>
  );
}

export default AdminLogin;
